import React, { useEffect, useState } from "react";
import {
    Table,
    Button,
    Input,
    Select,
    DatePicker,
    Spin,
    Row,
    Col,
    Space
} from "antd";
import { Link } from "react-router-dom";
import TablePagination from "../TablePagination";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/es/table";

import { fetchMediatorScores } from "../../api/mediator";
import MediatorScoresResponse from "../../types/MediatorScoresResponse";
import { formatDateToFullString, parseDate } from "../../util/date";
import { parseNumberToString } from "../../util/common";
import { SelectedFiltersType } from "./types";
import { selectedFiltersInitialState } from "./states";
import { SCORE_TYPES } from "./data";
import { useTexts } from "../../hooks/useTexts";

const MediatorScores = () => {
    const { t } = useTexts();
    const [selectedFilters, setSelectedFilters] = useState<SelectedFiltersType>(selectedFiltersInitialState);
    const [state, setState] = useState<MediatorScoresResponse>({
        mediatorScores: [],
        pagination: {
            page: 0,
            pageSize: 10,
            pageCount: 0,
            elementCount: 0,
        },
    });
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const parseType = (type: string) => {
        switch (type) {
            case "INITIAL":
                return t('mediator', 'initialScore');
            case "GIFT":
                return t('mediator', 'giftScore');
            case "OTHER":
                return t('mediator', 'otherScore');
            default:
                return null;
        }
    };

    const getParams = (filters: SelectedFiltersType) => ({
        dateFrom: parseDate(filters.dateFrom),
        dateTo: parseDate(filters.dateTo),
        page: filters.page ?? null,
        pageSize: filters.pageSize ?? null,
        firstName: filters.firstName ?? null,
        lastName: filters.lastName ?? null,
        type: filters.type ?? null,
    });

    const setValue = (name: string, value: string) => {
        setSelectedFilters((prev) => ({ ...prev, [name]: value }));
    };

    const filterInputOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.name, e.target.value);
    };

    const fetchData = async (filters: SelectedFiltersType) => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetchMediatorScores(getParams(filters));
            if (response && Array.isArray(response.mediatorScores)) {
                setState(response);
                if (response.pagination) {
                    setSelectedFilters((prev) => ({
                        ...prev,
                        page: response.pagination.page,
                        pageSize: response.pagination.pageSize,
                    }));
                }
            } else if (response) {
                // API returned an error body — keep existing state, show error
                setError(t('common', 'fetchError'));
            }
        } catch (err) {
            console.error(err);
            setError(t('common', 'genericError'));
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => fetchData(selectedFilters);

    const clearFilter = () => {
        setSelectedFilters(selectedFiltersInitialState);
        fetchData(selectedFiltersInitialState);
    };

    useEffect(() => {
        fetchData(selectedFilters);
    }, []);

    const columns: ColumnsType<any> = [
        {
            title: '#',
            key: 'index',
            render: (text, record, index) => ((state.pagination.page - 1) * state.pagination.pageSize) + index + 1,
            width: 60,
        },
        {
            title: t('prescription', 'mediatorFirstname'),
            dataIndex: 'mediatorFullName',
            key: 'mediatorFullName',
            render: text => text ?? '-'
        },
        {
            title: t('prescription', 'prescription'),
            key: 'prescription',
            render: (_, record) => record.prescriptionId ? (
                <Link to={`/prescriptions/${record.prescriptionId}`}>{t('prescription', 'viewPrescription')}</Link>
            ) : "-"
        },
        {
            title: t('prescription', 'score'),
            key: 'score',
            render: (_, record) => record.score != null ? parseNumberToString(record.score) : '-'
        },
        {
            title: t('common', 'type'),
            key: 'type',
            render: (_, record) => parseType(record.type) ?? '-'
        },
        {
            title: t('user', 'fullName'),
            dataIndex: 'userFullName',
            key: 'userFullName',
            render: text => text ?? '-'
        },
        {
            title: t('prescription', 'createdAt'),
            key: 'createdAt',
            render: (_, record) => record.createdAt ? formatDateToFullString(record.createdAt) : "-"
        }
    ];

    return (
        <div style={{ marginTop: "50px" }}>
            <Row gutter={[16, 16]} align="middle" style={{ marginBottom: "16px" }}>
                <Col xs={24} sm={12} md={6}>
                    <Input
                        placeholder={t('user', 'firstname')}
                        name="firstName"
                        value={selectedFilters.firstName || ""}
                        onChange={filterInputOnChange}
                        allowClear
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Input
                        placeholder={t('user', 'lastname')}
                        name="lastName"
                        value={selectedFilters.lastName || ""}
                        onChange={filterInputOnChange}
                        allowClear
                    />
                </Col>
                <Col xs={24} sm={12} md={4}>
                    <Select
                        placeholder={t('common', 'type')}
                        value={selectedFilters.type || undefined}
                        onChange={(value) => setValue('type', value)}
                        style={{ width: '100%' }}
                        allowClear
                        options={[
                            ...SCORE_TYPES.map(item => ({
                                label: t('mediator', item.key),
                                value: item.value
                            }))
                        ]}
                    />
                </Col>
                <Col xs={24} sm={12} md={4}>
                    <DatePicker
                        placeholder={t('common', 'startDate')}
                        value={selectedFilters.dateFrom ? dayjs(selectedFilters.dateFrom) : null}
                        onChange={(date) => setSelectedFilters((prev) => ({ ...prev, dateFrom: date }))}
                        style={{ width: '100%' }}
                        format="DD/MM/YYYY"
                    />
                </Col>
                <Col xs={24} sm={12} md={4}>
                    <DatePicker
                        placeholder={t('common', 'endDate')}
                        value={selectedFilters.dateTo ? dayjs(selectedFilters.dateTo) : null}
                        onChange={(date) => setSelectedFilters((prev) => ({ ...prev, dateTo: date }))}
                        style={{ width: '100%' }}
                        format="DD/MM/YYYY"
                    />
                </Col>
            </Row>

            <Row justify="end" style={{ marginBottom: "24px" }}>
                <Space>
                    <Button
                        type="primary"
                        onClick={applyFilters}
                        loading={loading}
                    >
                        {t('prescription', 'applyFilter')}
                    </Button>
                    <Button danger onClick={clearFilter}>
                        {t('prescription', 'clearFilter')}
                    </Button>
                </Space>
            </Row>

            {error ? (
                <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>
            ) : (
                <div style={{ background: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                    <Table
                        columns={columns}
                        dataSource={state.mediatorScores ?? []}
                        rowKey={(record: any) => record.id || Math.random().toString()}
                        scroll={{ x: 'max-content' }}
                        loading={loading}
                        pagination={false}
                    />
                    <TablePagination
                        current={state.pagination.page}
                        pageSize={state.pagination.pageSize}
                        pageCount={state.pagination.pageCount}
                        total={state.pagination.elementCount}
                        onChange={(page, pageSize) => fetchData({ ...selectedFilters, page, pageSize })}
                    />
                </div>
            )}
        </div>
    );
};

export default MediatorScores;
